import React, { useState, useEffect, useRef } from 'react';
import { 
  X, Mail, Clock, CheckCircle, Eye, Send, MessageCircle, 
  Paperclip, FileText, Image as ImageIcon, Video as VideoIcon, 
  ChevronDown, ChevronUp 
} from 'react-feather';
import { integrationsAPI } from '../services/integrationsAPI';
import { useAuth } from '../contexts/AuthContext';
import { ThreadReplyModal } from './ThreadReplyModal';
import { useResponsive } from '../hooks/useResponsive';

interface GmailMessage {
  message_id: string;
  thread_id: string;
  to_emails: string[];
  from_email?: string;
  subject: string;
  body?: string;
  body_html?: string;
  timestamp: string;
  tracking_token?: string;
  is_opened: boolean;
  opened_at?: string;
  attachments?: Array<{
    url: string;
    size: number;
    filename: string;
    content_type: string;
  }>;
  threadData?: any;
  threadReplies?: any;
  unreadCount?: number;
}

interface GmailDetailModalProps {
  visible: boolean;
  onClose: () => void;
  message: GmailMessage | null;
  athleteId?: string;
  onThreadUpdate?: (updatedMessage: GmailMessage) => void;
}

export const GmailDetailModal: React.FC<GmailDetailModalProps> = ({
  visible,
  onClose,
  message,
  athleteId: propAthleteId,
  onThreadUpdate,
}) => {
  const { sessionToken } = useAuth();
  const { isMobile } = useResponsive();
  const [activeTab, setActiveTab] = useState<'overview' | 'thread'>('overview');
  const [technicalDetailsExpanded, setTechnicalDetailsExpanded] = useState(false);
  const [athleteId, setAthleteId] = useState<string | null>(propAthleteId || null);
  const [localMessage, setLocalMessage] = useState<GmailMessage | null>(message);
  const [replyModalVisible, setReplyModalVisible] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Sync athleteId from props
  useEffect(() => {
    if (propAthleteId) {
      setAthleteId(propAthleteId);
    }
  }, [propAthleteId]);

  // Initialize tab based on thread data
  useEffect(() => {
    if (message) {
      const hasThreadData = message.threadData?.messages && message.threadData.messages.length > 1;
      setActiveTab(hasThreadData ? 'thread' : 'overview');
      setLocalMessage(message);
    }
  }, [message, visible]);

  // Mark thread messages as read
  const markThreadMessagesAsRead = async (currentAthleteId: string, threadMessages: any[]) => {
    try {
      console.log(`Marking ${threadMessages.length} messages as read in thread`);
      
      const markReadPromises = threadMessages.map(async (threadMessage) => {
        if (threadMessage.message_id) {
          try {
            await integrationsAPI.markMessageAsRead(currentAthleteId, threadMessage.message_id);
          } catch (error) {
            console.error(`Failed to mark message ${threadMessage.message_id} as read:`, error);
          }
        }
      });

      await Promise.allSettled(markReadPromises);
    } catch (error) {
      console.error('Error marking thread messages as read:', error);
    }
  };

  // Initialize and mark as read
  useEffect(() => {
    const initializeAndMarkRead = async () => {
      if (!visible || !localMessage || !athleteId) return;

      const messages = localMessage.threadData?.messages || localMessage.threadData?.data?.messages;
      if (messages && Array.isArray(messages)) {
        await markThreadMessagesAsRead(athleteId, messages);
      }
    };

    initializeAndMarkRead();
  }, [visible, localMessage, athleteId]);

  // Scroll to bottom for thread view
  useEffect(() => {
    if (activeTab === 'thread' && contentRef.current) {
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.scrollTop = contentRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [activeTab, localMessage?.threadData?.messages]);

  // Handle successful reply - refresh thread data
  const handleReplySuccess = async () => {
    if (!athleteId || !localMessage?.thread_id) return;

    try {
      console.log('Refreshing thread data after successful reply');
      
      // Fetch updated thread data
      const updatedThreadData = await integrationsAPI.getGmailThreadReplies(athleteId, localMessage.thread_id);
      
      if (updatedThreadData.success && updatedThreadData.data) {
        const updatedMessage = {
          ...localMessage,
          threadData: updatedThreadData.data,
        };
        
        setLocalMessage(updatedMessage);
        
        // Notify parent component of the update
        if (onThreadUpdate) {
          onThreadUpdate(updatedMessage);
        }
      }
    } catch (error) {
      console.error('Error refreshing thread data:', error);
    }
  };

  if (!visible || !localMessage) return null;

  const hasThreadData = localMessage.threadData?.messages && localMessage.threadData.messages.length > 1;

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('image/')) return ImageIcon;
    if (contentType.startsWith('video/')) return VideoIcon;
    if (contentType.includes('pdf') || contentType.includes('word') || contentType.includes('document')) return FileText;
    return Paperclip;
  };

  // HTML email body → readable plain text that PRESERVES line/paragraph breaks.
  // The old version stripped every tag then collapsed `\s+` → ' ', which flattened
  // the whole body onto one line (and broke removeQuotedReply, which splits on \n).
  // Kept in lockstep with the mobile GmailDetailModal cleaner (APS_RN).
  const cleanHtmlContent = (html: string): string => {
    if (!html) return '';

    // Tags must be replaced with WHITESPACE, not deleted — stripping them outright
    // welds adjacent cells together (a templated email became one long run because
    // <td>Softball</td><td>Pitcher</td> collapsed to "SoftballPitcher").
    let cleaned = html
      // script/style bodies + conditional comments (<!--[if mso]>…) are not content
      .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, '')
      .replace(/<!--[\s\S]*?-->/g, '')
      // block-level boundaries become line breaks
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|tr|li|h[1-6]|table|blockquote|section)\s*>/gi, '\n')
      // cell boundaries are word breaks, not line breaks
      .replace(/<\/(td|th)\s*>/gi, ' ')
      // anything else leaves a space behind it
      .replace(/<[^>]*>/g, ' ');

    // Decode HTML entities (named + numeric).
    cleaned = cleaned
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/&#(\d+);/g, (_m, n) => String.fromCharCode(parseInt(n, 10)));

    // Normalise WITHOUT destroying paragraphs: collapse runs of spaces/tabs, keep
    // newlines, cap blank lines at one. (The old /\s+/ → ' ' flattened newlines too.)
    cleaned = cleaned
      .replace(/[ \t\f\v]+/g, ' ')
      .replace(/ *\n */g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    return cleaned;
  };

  const removeQuotedReply = (text: string): string => {
    if (!text) return '';
    
    const lines = text.split('\n');
    const filteredLines = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();
      
      if (trimmedLine.startsWith('>')) continue;
      
      if (trimmedLine.includes('--- Original Message ---') || 
          trimmedLine.includes('----Original Message----') ||
          trimmedLine.includes('----- Original Message -----')) {
        break;
      }
      
      if (trimmedLine.match(/^(From|To|Sent|Date|Subject):\s/i)) {
        const nextLine = i + 1 < lines.length ? lines[i + 1].trim() : '';
        const prevLine = i > 0 ? lines[i - 1].trim() : '';
        
        if (nextLine.match(/^(From|To|Sent|Date|Subject):\s/i) || 
            prevLine.match(/^(From|To|Sent|Date|Subject):\s/i)) {
          break;
        }
      }
      
      if (trimmedLine.match(/^On .+, .+ wrote:$/i)) break;
      
      filteredLines.push(line);
    }
    
    return filteredLines.join('\n').trim();
  };

  const getEmailContent = (): string => {
    let content = '';
    
    if (localMessage?.body_html) {
      content = cleanHtmlContent(localMessage.body_html);
    } else if (localMessage?.body) {
      content = localMessage.body;
    } else {
      return 'No content available';
    }
    
    return removeQuotedReply(content);
  };

  const threadMessages = localMessage.threadData?.messages || localMessage.threadData?.data?.messages || [];

  // Normalize thread data for the reply composer. For a single sent email (no
  // real thread yet) synthesize a one-message thread from the email itself so
  // the reply affordance on the overview tab still works (#210).
  const replyThreadData = {
    thread_id: localMessage.thread_id,
    subject: localMessage.subject,
    messages: threadMessages.length > 0 ? threadMessages : [{
      message_id: localMessage.message_id,
      from_email: localMessage.from_email || '',
      to_emails: localMessage.to_emails || [],
      subject: localMessage.subject,
      body: localMessage.body || '',
      timestamp: localMessage.timestamp,
      is_read: localMessage.is_opened,
    }],
  };

  return (
    <div 
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001,
        padding: isMobile ? '16px' : '20px',
      }}
    >
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '16px',
          width: isMobile ? 'calc(100vw - 32px)' : '100%',
          maxWidth: isMobile ? 'calc(100vw - 32px)' : '900px',
          maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: isMobile ? '16px' : '24px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#151133',
          fontFamily: 'Manrope, sans-serif'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: '700',
            color: 'white'
          }}>
            Email Details
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <X size={24} color="white" />
          </button>
        </div>

        {/* Tab Navigation */}
        {hasThreadData && (
          <div style={{
            display: 'flex',
            borderBottom: '1px solid #E5E7EB',
            background: '#F9FAFB'
          }}>
            <button
              onClick={() => setActiveTab('overview')}
              style={{
                flex: 1,
                padding: '16px',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === 'overview' ? '2px solid #F9550D' : '2px solid transparent',
                fontSize: '14px',
                fontWeight: '600',
                color: activeTab === 'overview' ? '#F9550D' : '#6B7280',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontFamily: 'Manrope, sans-serif'
              }}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('thread')}
              style={{
                flex: 1,
                padding: '16px',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === 'thread' ? '2px solid #F9550D' : '2px solid transparent',
                fontSize: '14px',
                fontWeight: '600',
                color: activeTab === 'thread' ? '#F9550D' : '#6B7280',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontFamily: 'Manrope, sans-serif'
              }}
            >
              Thread View
            </button>
          </div>
        )}

        {/* Content */}
        <div
          ref={contentRef}
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '24px',
            background: '#FAFBFF'
          }}
        >
          {activeTab === 'overview' ? (
            <>
              {/* Status Badge */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '24px'
              }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  background: localMessage.is_opened ? '#10B981' : 'var(--aps-text-2)',
                  color: 'white'
                }}>
                  {localMessage.is_opened ? <CheckCircle size={16} /> : <Clock size={16} />}
                  <span style={{ fontSize: '14px', fontWeight: '600' }}>
                    {localMessage.is_opened ? 'Opened' : 'Not Opened Yet'}
                  </span>
                </div>
              </div>

              {/* Subject */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #E5E7EB'
              }}>
                <h3 style={{
                  margin: '0 0 12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#111827'
                }}>
                  Subject
                </h3>
                <p style={{
                  margin: 0,
                  fontSize: '16px',
                  color: '#1F2937',
                  lineHeight: '1.6'
                }}>
                  {localMessage.subject}
                </p>
              </div>

              {/* Message Content */}
              {(localMessage.body_html || localMessage.body) && (
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  border: '1px solid #E5E7EB'
                }}>
                  <h3 style={{
                    margin: '0 0 12px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#111827'
                  }}>
                    Message Content
                  </h3>
                  <div style={{
                    background: '#F8FAFC',
                    borderRadius: '8px',
                    padding: '16px',
                    border: '1px solid #E5E7EB',
                    maxHeight: '300px',
                    overflow: 'auto'
                  }}>
                    <p style={{
                      margin: 0,
                      fontSize: '15px',
                      color: '#1F2937',
                      lineHeight: '1.8',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {getEmailContent()}
                    </p>
                  </div>
                </div>
              )}

              {/* Recipients */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #E5E7EB'
              }}>
                <h3 style={{
                  margin: '0 0 12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#111827'
                }}>
                  Sent To
                </h3>
                {localMessage.to_emails.map((email, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: index < localMessage.to_emails.length - 1 ? '8px' : 0
                    }}
                  >
                    <Mail size={16} color="#EA4335" />
                    <span style={{ fontSize: '15px', color: '#1F2937', fontWeight: '500' }}>
                      {email}
                    </span>
                  </div>
                ))}
              </div>

              {/* Timing Information */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #E5E7EB'
              }}>
                <h3 style={{
                  margin: '0 0 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#111827'
                }}>
                  Timing
                </h3>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  <Send size={16} color="#6B7280" style={{ marginTop: '2px' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>Sent</div>
                    <div style={{ fontSize: '15px', color: '#1F2937', fontWeight: '500' }}>
                      {formatDate(localMessage.timestamp)}
                    </div>
                  </div>
                </div>

                {localMessage.is_opened && localMessage.opened_at && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    <Eye size={16} color="#10B981" style={{ marginTop: '2px' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>Opened</div>
                      <div style={{ fontSize: '15px', color: '#1F2937', fontWeight: '500' }}>
                        {formatDate(localMessage.opened_at)}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Performance Insights */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #E5E7EB'
              }}>
                <h3 style={{
                  margin: '0 0 12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#111827'
                }}>
                  Performance
                </h3>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  padding: '12px',
                  background: '#F8FAFC',
                  borderRadius: '8px',
                  borderLeft: `4px solid ${localMessage.is_opened ? '#10B981' : 'var(--aps-text-2)'}`
                }}>
                  {localMessage.is_opened ? (
                    <CheckCircle size={20} color="#10B981" style={{ marginTop: '2px' }} />
                  ) : (
                    <Clock size={20} style={{ marginTop: '2px', color: 'var(--aps-text-2)' }} />
                  )}
                  <p style={{
                    margin: 0,
                    fontSize: '14px',
                    color: '#4B5563',
                    lineHeight: '1.6'
                  }}>
                    {localMessage.is_opened 
                      ? 'Great! The coach opened your email. This shows genuine interest.'
                      : 'Email hasn\'t been opened yet. Consider following up or trying a different approach.'}
                  </p>
                </div>
              </div>

              {/* Thread Information */}
              {localMessage.threadData && (
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  border: '1px solid #E5E7EB'
                }}>
                  <h3 style={{
                    margin: '0 0 12px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#111827'
                  }}>
                    Thread Activity
                  </h3>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '12px',
                    background: '#FEF2F2',
                    borderRadius: '8px',
                    borderLeft: '4px solid #EA4335'
                  }}>
                    <MessageCircle size={20} color="#EA4335" style={{ marginTop: '2px' }} />
                    <p style={{
                      margin: 0,
                      fontSize: '14px',
                      color: '#4B5563',
                      lineHeight: '1.6'
                    }}>
                      {localMessage.threadData.messages && localMessage.threadData.messages.length > 1 
                        ? `This email is part of a conversation with ${localMessage.threadData.messages.length} messages.`
                        : 'This email thread has activity.'}
                    </p>
                  </div>
                </div>
              )}

              {/* Attachments */}
              {localMessage.attachments && localMessage.attachments.length > 0 && (
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  border: '1px solid #E5E7EB'
                }}>
                  <h3 style={{
                    margin: '0 0 12px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#111827'
                  }}>
                    Attachments ({localMessage.attachments.length})
                  </h3>

                  {/* Image/Video Preview Grid */}
                  {localMessage.attachments.some(att => att.content_type?.startsWith('image/') || att.content_type?.startsWith('video/')) && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: localMessage.attachments.filter(att => att.content_type?.startsWith('image/') || att.content_type?.startsWith('video/')).length === 1 ? '1fr' : 'repeat(2, 1fr)',
                      gap: '8px',
                      marginBottom: '16px',
                      borderRadius: '12px',
                      overflow: 'hidden'
                    }}>
                      {localMessage.attachments
                        .filter(att => att.content_type?.startsWith('image/') || att.content_type?.startsWith('video/'))
                        .slice(0, 4)
                        .map((attachment, idx) => {
                          const isVideo = attachment.content_type?.startsWith('video/');
                          return (
                            <a
                              key={idx}
                              href={attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                position: 'relative',
                                paddingBottom: localMessage.attachments!.filter(att => att.content_type?.startsWith('image/') || att.content_type?.startsWith('video/')).length === 1 ? '56.25%' : '100%',
                                backgroundColor: '#F3F4F6',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                textDecoration: 'none'
                              }}
                            >
                              {isVideo ? (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#F3F4F6'
                                  }}
                                >
                                  <VideoIcon size={32} color="#6B7280" />
                                </div>
                              ) : (
                                <>
                                  <img
                                    src={attachment.url}
                                    alt={attachment.filename}
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover'
                                    }}
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                      const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                                      if (placeholder) placeholder.style.display = 'flex';
                                    }}
                                  />
                                  <div
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      display: 'none',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: '#F3F4F6'
                                    }}
                                  >
                                    <ImageIcon size={32} color="#6B7280" />
                                  </div>
                                </>
                              )}
                            </a>
                          );
                        })}
                    </div>
                  )}

                  {/* File List for All Attachments */}
                  {localMessage.attachments.map((attachment, index) => {
                    const IconComponent = getFileIcon(attachment.content_type);
                    return (
                      <a
                        key={index}
                        href={attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '12px',
                          background: '#F9FAFB',
                          borderRadius: '8px',
                          border: '1px solid #E5E7EB',
                          textDecoration: 'none',
                          marginBottom: index < localMessage.attachments!.length - 1 ? '8px' : 0,
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#F3F4F6'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#F9FAFB'}
                      >
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '20px',
                          background: '#EBF5FF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '12px'
                        }}>
                          <IconComponent size={20} color="#007AFF" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: '14px',
                            color: '#1F2937',
                            fontWeight: '500',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {attachment.filename || 'Untitled'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                            {formatFileSize(attachment.size || 0)}
                          </div>
                        </div>
                      </a>
                    );
                  })}
                </div>
              )}

              {/* Technical Details */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #E5E7EB'
              }}>
                <button
                  onClick={() => setTechnicalDetailsExpanded(!technicalDetailsExpanded)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    marginBottom: technicalDetailsExpanded ? '12px' : 0
                  }}
                >
                  <h3 style={{
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#111827'
                  }}>
                    Technical Details
                  </h3>
                  {technicalDetailsExpanded ? <ChevronUp size={20} color="#6B7280" /> : <ChevronDown size={20} color="#6B7280" />}
                </button>
                
                {technicalDetailsExpanded && (
                  <div style={{
                    paddingTop: '12px',
                    borderTop: '1px solid #F3F4F6',
                    marginTop: '12px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: '1px solid #F3F4F6',
                      marginBottom: '8px'
                    }}>
                      <span style={{ fontSize: '13px', color: '#6B7280' }}>Message ID</span>
                      <span style={{
                        fontSize: '13px',
                        color: '#1F2937',
                        fontWeight: '500',
                        maxWidth: '50%',
                        textAlign: 'right',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {localMessage.message_id}
                      </span>
                    </div>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: '1px solid #F3F4F6',
                      marginBottom: localMessage.tracking_token ? '8px' : 0
                    }}>
                      <span style={{ fontSize: '13px', color: '#6B7280' }}>Thread ID</span>
                      <span style={{
                        fontSize: '13px',
                        color: '#1F2937',
                        fontWeight: '500',
                        maxWidth: '50%',
                        textAlign: 'right',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {localMessage.thread_id}
                      </span>
                    </div>

                    {localMessage.tracking_token && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 0'
                      }}>
                        <span style={{ fontSize: '13px', color: '#6B7280' }}>Tracking Token</span>
                        <span style={{
                          fontSize: '13px',
                          color: '#1F2937',
                          fontWeight: '500',
                          maxWidth: '50%',
                          textAlign: 'right',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {localMessage.tracking_token}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Thread View */}
              <div style={{ padding: '8px' }}>
                {threadMessages
                  .sort((a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
                  .map((threadMessage: any, index: number) => {
                    // TODO(audit #209): prefer the athlete's *connected Gmail*
                    // address (from integration status) over the tracked
                    // message's from_email. Comparing to localMessage.from_email
                    // (the athlete's own sent-from address) is already more
                    // reliable than assuming the first message in the thread is
                    // the athlete's.
                    const connectedGmail = (localMessage.from_email || '').toLowerCase();
                    const firstMessageSender = threadMessages.length > 0 ? threadMessages[0].from_email : null;
                    const isSentByUser = connectedGmail
                      ? (threadMessage.from_email || '').toLowerCase() === connectedGmail
                      : threadMessage.from_email === firstMessageSender;
                    
                    return (
                      <div
                        key={`${threadMessage.message_id || `msg-${index}`}-${threadMessage.timestamp || index}`}
                        style={{
                          maxWidth: '80%',
                          marginLeft: isSentByUser ? 'auto' : 0,
                          marginRight: isSentByUser ? 0 : 'auto',
                          marginBottom: '10px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: isSentByUser ? 'flex-end' : 'flex-start'
                        }}
                      >
                        <span style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          color: 'var(--aps-text-subtle)',
                          margin: '0 4px 3px'
                        }}>
                          {isSentByUser ? 'You' : threadMessage.from_email || 'Recipient'}
                        </span>
                        <div style={{
                          padding: '10px 14px',
                          borderRadius: isSentByUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                          background: isSentByUser ? 'var(--aps-action-tint)' : 'var(--aps-fill)',
                          border: isSentByUser ? '1px solid var(--aps-action-border)' : '1px solid var(--aps-border)',
                          color: 'var(--aps-ink)',
                          fontSize: '14px',
                          lineHeight: '1.5',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}>
                          {removeQuotedReply(threadMessage.body || threadMessage.subject || 'No content')}
                        </div>
                        <span style={{
                          fontSize: '11px',
                          color: 'var(--aps-text-subtle)',
                          margin: '4px 4px 0'
                        }}>
                          {threadMessage.timestamp ? new Date(threadMessage.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Unknown time'}
                        </span>
                      </div>
                    );
                  })}
              </div>
            </>
          )}
        </div>

        {/* Reply Footer — a sent email can always be followed up on (#210) */}
        <div style={{
          padding: '20px 24px',
          borderTop: '1px solid #E5E7EB',
          background: 'white'
        }}>
            <button
              onClick={() => setReplyModalVisible(true)}
              style={{
                width: '100%',
                padding: '14px 24px',
                background: '#EA4335',
                color: 'white',
                border: 'none',
                borderRadius: '24px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 2px 8px rgba(234, 67, 53, 0.3)',
                transition: 'all 0.2s',
                fontFamily: 'Manrope, sans-serif'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#D23426';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(234, 67, 53, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#EA4335';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(234, 67, 53, 0.3)';
              }}
            >
              <Mail size={16} />
              <span>Send Reply</span>
            </button>
        </div>
      </div>

      {/* Thread Reply Modal */}
      <ThreadReplyModal
        visible={replyModalVisible && !!athleteId && !!localMessage?.thread_id}
        onClose={() => setReplyModalVisible(false)}
        threadId={localMessage?.thread_id || ''}
        threadData={replyThreadData}
        athleteId={athleteId || ''}
        onSuccess={handleReplySuccess}
      />
    </div>
  );
};
